import type { ReactNode } from 'react';
import { About } from './About';

/**
 * The portal's shell-less screens: loading, the API unreachable, the staff player picker, and the
 * cards screen with the claim flow behind it. Most are a dead end — there is no navigation out of
 * them — which is precisely when being able to name the build matters, so the version line belongs
 * to this frame rather than only to the path that resolves a player.
 *
 * "Most", not "each", since ADR 0010: the cards screen is the one that goes somewhere, and it is
 * the screen that replaced the unlinked-account notice this comment used to name.
 *
 * `.onair` and the inline background are the same pairing `PortalShell` uses: the class
 * redefines the palette tokens for everything inside, and the inline style overrides its own
 * background so a screen keeps whichever ground it asked for. Without it the shared components
 * here — `Spinner`, `ErrorRetry`, and the About dialog — resolve to the light Rail palette on a
 * charcoal ground.
 *
 * `min-h-dvh` rather than `min-h-screen` because something is now pinned to the bottom: `100vh`
 * is measured with a phone's browser chrome retracted, which would put the version line under the
 * address bar on the one screen with nothing else to scroll to.
 */
export function CenteredScreen({
  background,
  color,
  children,
}: {
  background: string;
  color?: string;
  children: ReactNode;
}) {
  return (
    <div className="onair relative grid min-h-dvh place-items-center p-6" style={{ background, color }}>
      {children}
      <div className="absolute inset-x-0 bottom-0 flex justify-center pb-5">
        <About />
      </div>
    </div>
  );
}
